// Pista de volley (4x4 o 6x6) con la red en un extremo. En móvil se muestra
// en vertical (red arriba) y en escritorio en horizontal (red a la
// izquierda), que es como tiene más sentido verla en cada pantalla. Cada
// slot es un círculo clicable que muestra el jugador asignado (o un hueco
// vacío) y abre el selector al pulsarlo.
import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

export default function PistaVolley({ slots, asignaciones, jugadoresById, onSlotClick }) {
  const theme = useTheme();
  const esHorizontal = useMediaQuery(theme.breakpoints.up("sm"));

  const jugadorEnSlot = (slotId) => {
    const entrada = asignaciones.find((a) => a.slot === slotId);
    return entrada?.jugador_id ? jugadoresById[entrada.jugador_id] : null;
  };

  // Coordenadas canónicas pensadas en vertical (red arriba). En horizontal se
  // transponen para que la red quede a la izquierda.
  const posicion = (slot) => (esHorizontal ? { x: slot.y, y: slot.x } : { x: slot.x, y: slot.y });

  return (
    <Box
      sx={{
        position: "relative",
        // En vertical (móvil) se limita la altura para que quepa sin scroll;
        // el ancho lo calcula el navegador a partir del aspect-ratio.
        width: esHorizontal ? "100%" : "auto",
        height: esHorizontal ? "auto" : "38vh",
        maxWidth: "100%",
        mx: esHorizontal ? 0 : "auto",
        aspectRatio: esHorizontal ? "16 / 10" : "10 / 16",
        borderRadius: 3,
        overflow: "hidden",
        background: "linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)",
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.15)",
      }}
    >
      {/* Líneas de la pista: contorno, red y línea de ataque (3m) */}
      <Box
        component="svg"
        viewBox={esHorizontal ? "0 0 100 62.5" : "0 0 62.5 100"}
        preserveAspectRatio="none"
        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      >
        <g
          fill="none"
          stroke="rgba(255,255,255,0.6)"
          strokeWidth="0.6"
          vectorEffect="non-scaling-stroke"
        >
          {esHorizontal ? (
            <>
              <rect x="1" y="1" width="98" height="60.5" rx="1.5" />
              <line
                x1="1"
                y1="1"
                x2="1"
                y2="61.5"
                strokeWidth="1.6"
                stroke="rgba(255,255,255,0.85)"
              />
              <line x1="34" y1="1" x2="34" y2="61.5" strokeDasharray="2,1.5" />
            </>
          ) : (
            <>
              <rect x="1" y="1" width="60.5" height="98" rx="1.5" />
              <line
                x1="1"
                y1="1"
                x2="61.5"
                y2="1"
                strokeWidth="1.6"
                stroke="rgba(255,255,255,0.85)"
              />
              <line x1="1" y1="34" x2="61.5" y2="34" strokeDasharray="2,1.5" />
            </>
          )}
        </g>
      </Box>

      {/* Slots de jugadores */}
      {slots.map((slot) => {
        const jugador = jugadorEnSlot(slot.id);
        const pos = posicion(slot);
        return (
          <Tooltip key={slot.id} title={jugador ? jugador.nombre : `Asignar ${slot.label}`}>
            <Box
              onClick={(e) => onSlotClick(slot, e)}
              sx={{
                position: "absolute",
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
                userSelect: "none",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: { xs: 38, sm: 46 },
                  height: { xs: 38, sm: 46 },
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  color: jugador ? "#1B2A55" : "#fff",
                  bgcolor: jugador ? "#fff" : alpha("#fff", 0.18),
                  border: "2px solid",
                  borderColor: jugador ? "#fff" : alpha("#fff", 0.55),
                  boxShadow: jugador ? "0 2px 6px rgba(0,0,0,0.35)" : "none",
                  transition: "transform .15s ease",
                  "&:hover": { transform: "scale(1.08)" },
                }}
              >
                {jugador ? jugador.numero ?? jugador.nombre?.[0] ?? "?" : "+"}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: { xs: "0.6rem", sm: "0.68rem" },
                  textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                  maxWidth: 72,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {jugador ? jugador.nombre.split(" ")[0] : slot.label}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

PistaVolley.propTypes = {
  slots: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    })
  ).isRequired,
  asignaciones: PropTypes.arrayOf(
    PropTypes.shape({ slot: PropTypes.string, jugador_id: PropTypes.string })
  ).isRequired,
  jugadoresById: PropTypes.object.isRequired,
  onSlotClick: PropTypes.func.isRequired,
};
